<template>
  <v-row
    v-if="errors.fetching"
    style="margin-top: 50px"
    no-gutters
    align="center"
    justify="center"
  >
    <v-card width="800" height="700" class="mb-16" flat>
      <v-row no-gutters justify="center" align="center">
        <v-col
          cols="8"
          class="d-flex justify-start align-start mb-3 flex-column"
        >
          <span class="text-h6"
            >{{ $t('message.videoApiError') }}
            {{ this.unknownErrorCode }}).</span
          >
          <br />
          <span class="text-subheading">
            {{ $t('message.videoApiErrMsg') }} <br />
            <button
              type="button"
              class="primary--text"
              @click="handleStatusPage"
            >
              <span class="primary--text">
                {{ $t('message.checkStatusPageLink') }}
              </span>
            </button>
          </span>
          <br />
        </v-col>
        <v-col cols="12">
          <v-row no-gutters align="center" justify="center">
            <v-btn
              color="primary"
              class="ma-2 ml-3 white--text"
              rounded
              @click="
                pageRequests()
                handlePageRequests()
              "
            >
              <span class="mx-2">{{ $t('message.tryAgain') }}</span>
              <v-icon right dark> mdi-cached </v-icon>
            </v-btn>
          </v-row>
        </v-col>
      </v-row>
    </v-card>
  </v-row>

  <page-view v-else class="new-test" :is-loading="loadings.isLoadingVideos">
    <template #header>
      <base-header
        :title="comparison.name"
        :data-intro="$t('message.introJS.testAbNew.title')"
        has-intro
        @back="handleBackToListing"
      >
        <template #subtitle>
          <span
            class="status-button"
            :class="getStatus(currentStatus).classColor"
          >
            {{ getStatus(currentStatus).label }}
          </span>
        </template>

        <template #actions>
          <!-- embed -->
          <base-btn
            v-if="currentStatus !== 'DRAFT'"
            :data-intro="$t('message.introJS.testAbNew.embed')"
            @click="handleOpenCodeModal"
          >
            EMBED
          </base-btn>

          <!-- start -->
          <base-btn
            v-if="!isTestStarted"
            :loading="loadings.isChangingStatus"
            :disabled="!canTestBeStarted"
            :data-intro="$t('message.introJS.testAbNew.start')"
            @click="handleInitTest"
          >
            {{ $t('message.init').toUpperCase() }}
          </base-btn>

          <!-- edit name -->
          <base-btn
            v-if="hasEditAbTestPerm"
            type="secondary"
            @click="openChangeTitleModal = true"
          >
            <v-icon>mdi-pencil</v-icon>
          </base-btn>
        </template>
      </base-header>
    </template>

    <template #filter>
      <date-picker
        :shortcuts="shortcuts"
        :clearable="false"
        :disabled-date="verifyDisabledDate"
        popup-class="testABDatePicker"
        style="width: 260px"
        v-model="analyticsDates"
        editable
        confirm
        range
      >
        <template v-slot:input>
          <v-text-field
            :class="{ testABPickerField: $vuetify.theme.dark }"
            dense
            :value="dateRangeValue"
            outlined
            prepend-inner-icon="mdi-calendar-month"
            readonly
            hide-details
          >
            <template v-slot:prepend-inner>
              <v-icon color="primary" class="mr-2"> mdi-calendar-month </v-icon>
            </template>
          </v-text-field>
        </template>
        <template v-slot:icon-calendar>
          <v-icon class="primary" style="display: none" color="primary">
            mdi-calendar-month
          </v-icon>
        </template>
      </date-picker>
    </template>

    <template #content>
      <v-card
        v-if="loadings.rangeUpdate"
        flat
        height="250px"
        class="d-flex flex-row align-center justify-center"
      >
        <loading-logo :value="200" />
      </v-card>
      <span v-show="!loadings.rangeUpdate">
        <v-row flat v-if="hasEditAbTestPerm">
          <v-col class="d-flex justify-end">
            <v-btn
              v-if="currentStatus === 'DRAFT' && selectedItems.length >= 1"
              icon
              fab
              color="primary"
              @click="handleDelete"
              outlined
            >
              <v-icon> mdi-delete-outline </v-icon>
            </v-btn>
            <v-btn
              v-if="currentStatus === 'DRAFT'"
              icon
              fab
              class="ml-4 primary white--text"
              :data-intro="$t('message.introJS.testAbNew.add')"
              @click="handleAddVideo"
            >
              <v-icon> mdi-plus </v-icon>
            </v-btn>
          </v-col>
        </v-row>
        <v-row v-if="currentStatus !== 'DRAFT' && !loadings.isLoadingAnalytics">
          <v-col>
            <v-container class="pa-0 chart-container d-flex" fluid>
              <div class="grid-widget-container">
                <div
                  class="grid-widget"
                  :data-intro="$t('message.introJS.testAbNew.playRate')"
                >
                  <apexchart
                    class="the-retention-chart"
                    ref="playrateChart"
                    type="area"
                    height="370"
                    width="100%"
                    :options="playrateChart.chartOptions"
                    :series="playrateChart.chartSeries"
                  ></apexchart>
                </div>
                <div
                  class="grid-widget"
                  :data-intro="$t('message.introJS.testAbNew.retention')"
                >
                  <apexchart
                    class="the-retention-chart"
                    ref="retentionChart"
                    type="area"
                    height="376"
                    width="100%"
                    :options="retentionChart.chartOptions"
                    :series="retentionChart.chartSeries"
                  />
                </div>
              </div>
            </v-container>
          </v-col>
        </v-row>
        <div
          class="my-4 d-flex mx-4"
          no-gutters
          style="gap: 10px; flex-wrap: wrap"
        >
          <v-card
            v-if="!loadings.isLoadingVideos && videos.length > 0"
            flat
            width="100%"
            height="35px"
          >
            <v-row no-gutters>
              <v-col
                class="d-flex align-center justify-start"
                style="padding-left: 5px; max-width: 32px"
                v-if="currentStatus === 'DRAFT'"
              >
                <v-row no-gutters>
                  <v-simple-checkbox
                    :color="!checkboxIndeterminateStatus ? 'primary' : ''"
                    :indeterminate="checkboxIndeterminateStatus"
                    :ripple="false"
                    :value="anyItemSelected"
                    @input="selectAllItems"
                  ></v-simple-checkbox>
                </v-row>
              </v-col>
              <v-col
                class="d-flex align-center justify-start"
                :style="getHeaderStyle('name')"
              >
                <v-hover v-slot="{ hover }">
                  <v-row no-gutters @click="handleOrderByName">
                    <span class="caption mt-1" style="height: 24px">
                      <b>{{
                        $t('message.pages.videosDataTable.header.name')
                      }}</b>
                    </span>
                    <v-icon v-if="hover && !nameOrder" color="grey lighten-1"
                      >mdi-menu-down</v-icon
                    >
                    <v-icon v-if="hover && nameOrder === 'desc'"
                      >mdi-menu-down</v-icon
                    >
                    <v-icon v-if="hover && nameOrder === 'asc'"
                      >mdi-menu-up</v-icon
                    >
                  </v-row>
                </v-hover>
              </v-col>
              <v-card
                class="d-flex align-center pl-2"
                height="35px"
                flat
                style="flex: 7.1 1 0%"
              >
                <v-hover v-slot="{ hover }">
                  <v-row no-gutters @click="handleorderByEngage">
                    <span class="caption mt-1" style="height: 24px">
                      <b>{{
                        $t('message.pages.video.metricsPage.retention')
                      }}</b>
                      <v-icon
                        class="ml-1"
                        size="20"
                        style="height: fit-content; margin: auto"
                        @click="openRetentionArticle"
                      >
                        mdi-help-circle
                      </v-icon>
                    </span>
                    <v-icon v-if="hover && !engageOrder" color="grey lighten-1"
                      >mdi-menu-down</v-icon
                    >
                    <v-icon v-if="hover && engageOrder === 'desc'"
                      >mdi-menu-down</v-icon
                    >
                    <v-icon v-if="hover && engageOrder === 'asc'"
                      >mdi-menu-up</v-icon
                    >
                  </v-row>
                </v-hover>
              </v-card>
              <v-card
                class="d-flex align-center pl-2"
                height="35px"
                flat
                style="flex: 8.5 1 0%"
              >
                <v-hover v-slot="{ hover }">
                  <v-row no-gutters @click="handleOrderByPlayrate">
                    <span class="caption mt-1" style="height: 24px">
                      <b>{{ $t('message.pages.AbTest.playrate') }}</b>
                      <v-icon
                        class="ml-1"
                        size="20"
                        style="height: fit-content; margin: auto"
                        @click="openAnalyticsArticle"
                      >
                        mdi-help-circle
                      </v-icon>
                    </span>
                    <v-icon
                      v-if="hover && !playrateOrder"
                      color="grey lighten-1"
                      >mdi-menu-down</v-icon
                    >
                    <v-icon v-if="hover && playrateOrder === 'desc'"
                      >mdi-menu-down</v-icon
                    >
                    <v-icon v-if="hover && playrateOrder === 'asc'"
                      >mdi-menu-up</v-icon
                    >
                  </v-row>
                </v-hover>
              </v-card>
              <v-card
                class="d-flex align-center pl-2"
                height="35px"
                flat
                :style="getHeaderStyle('views')"
              >
                <v-hover v-slot="{ hover }">
                  <v-row no-gutters @click="handleOrderByViews">
                    <span class="caption mt-1" style="height: 24px">
                      <b>{{ $t('message.pages.AbTest.views') }}</b>
                      <v-icon
                        class="ml-1"
                        size="20"
                        style="height: fit-content; margin: auto"
                        @click="openAnalyticsArticle"
                      >
                        mdi-help-circle
                      </v-icon>
                    </span>
                    <v-icon v-if="hover && !viewsOrder" color="grey lighten-1"
                      >mdi-menu-down</v-icon
                    >
                    <v-icon v-if="hover && viewsOrder === 'desc'"
                      >mdi-menu-down</v-icon
                    >
                    <v-icon v-if="hover && viewsOrder === 'asc'"
                      >mdi-menu-up</v-icon
                    >
                  </v-row>
                </v-hover>
              </v-card>
            </v-row>
          </v-card>
          <vue-virtual-table
            v-if="videos.length > 0"
            class="pointerTable"
            :class="{
              virtualTable: $vuetify.theme.dark,
            }"
            :config="tableConfig"
            :data="videos"
            :height="tableHeight"
            :itemHeight="48"
            :minWidth="1000"
            :data-intro="$t('message.introJS.testAbNew.list')"
          >
            <template
              slot-scope="scope"
              slot="titleCommon"
              style="align-items: flex-start !important"
            >
              <v-simple-checkbox
                v-if="currentStatus === 'DRAFT'"
                color="primary"
                :ripple="false"
                :value="checkIfSelected(scope.row)"
                @input="checkboxSelection(scope.row)"
              ></v-simple-checkbox>
              <v-row class="pl-2" no-gutters justify="start" align="center">
                <v-icon
                  v-if="currentStatus !== 'DRAFT'"
                  style="margin-right: 5px"
                  :color="scope.row.color"
                  @click="handleGoToVideo(scope.row.id)"
                >
                  mdi-play-circle-outline
                </v-icon>
                <v-icon
                  v-else
                  style="margin-right: 5px"
                  color="primary"
                  @click="handleGoToVideo(scope.row.id)"
                >
                  mdi-play-circle-outline
                </v-icon>
                <span
                  :title="scope.row.title"
                  class="text-truncate"
                  style="max-width: 254px"
                >
                  {{ scope.row.title }}
                </span>
                <template
                  v-if="
                    scope.row.status === 'CONVERTING' ||
                    scope.row.status === 'DRAFT'
                  "
                >
                  <span
                    v-if="
                      Math.abs(new Date(scope.row.created_at) - new Date()) /
                        36e5 <
                      3
                    "
                  >
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <span
                          v-on="on"
                          v-bind="attrs"
                          class="ml-2 secondaryText--text caption noIbar"
                        >
                          <v-icon>mdi-progress-upload</v-icon>
                        </span>
                      </template>
                      {{ $t('message.converting') }}...
                    </v-tooltip>
                  </span>
                  <span
                    v-else-if="
                      Math.abs(new Date(scope.row.created_at) - new Date()) /
                        36e5 <
                      48
                    "
                  >
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <span
                          v-on="on"
                          v-bind="attrs"
                          class="ml-2 text--text caption noIbar"
                        >
                          <v-icon>mdi-progress-upload</v-icon>
                        </span>
                      </template>
                      {{ $t('message.longerThanUsual') }}
                      <span
                        class="primary--text pointer-click"
                        @click="openKnowMoreConverting"
                        >{{ $t('message.knowMore') }} &raquo;
                      </span>
                    </v-tooltip>
                  </span>
                  <span v-else>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <span
                          v-bind="attrs"
                          v-on="on"
                          class="red--text ml-2 noIbar caption"
                        >
                          <v-icon size="18" color="red">
                            mdi-alert-circle-outline
                          </v-icon>
                          {{ $t('message.processingError') }}
                        </span>
                      </template>
                      <span>
                        {{ $t('message.verifyFileFormat') }}
                      </span>
                    </v-tooltip>
                  </span>
                </template>
              </v-row>
            </template>

            <template slot-scope="scope" slot="engageCommom">
              <v-row
                v-if="shouldShowListingAnalytics && scope.row.analytics"
                class="pt-1"
                :class="locale === 'pt' ? 'pl-3' : 'pl-4'"
                no-gutters
                justify="start"
                align="center"
              >
                {{ scope.row.analytics.retention }}%
              </v-row>
            </template>

            <template slot-scope="scope" slot="playrateCommom">
              <v-row
                class="pt-1"
                no-gutters
                justify="center"
                align="center"
                v-if="shouldShowListingAnalytics && scope.row.analytics"
              >
                <span class="text-center">
                  {{ scope.row.analytics.playRate }}%
                </span>
              </v-row>
            </template>

            <template
              slot-scope="scope"
              style="position: relative"
              slot="viewsCommom"
            >
              <v-row class="pt-1" no-gutters justify="center" align="center">
                <v-btn
                  v-if="currentStatus === 'IN_PROGRESS'"
                  @click="handleOpenWinnerModal(scope.index)"
                  color="primary"
                  text
                  dense
                  small
                  class="mb-1 declare-winner-button"
                >
                  {{ $t('message.pages.AbTest.chooseWinner').toUpperCase() }}
                </v-btn>
                <span
                  v-if="shouldShowListingAnalytics && scope.row.analytics"
                  class="text-center"
                  :class="currentStatus !== 'COMPLETED' ? 'views-info' : ''"
                >
                  {{ scope.row.analytics.views }}
                </span>
              </v-row>
              <div
                v-if="scope.row.id === winnerVideoId"
                style="position: absolute; right: 30px"
              >
                <v-icon :color="findColor(scope.row.id)">
                  mdi-crown-circle
                </v-icon>
              </div>
            </template>
          </vue-virtual-table>

          <v-container
            v-else-if="videos.length === 0"
            fill-height
            class="ma-0 pt-0 empty__container"
            style="width: 100%"
            :data-intro="$t('message.introJS.testAbNew.listEmpty')"
          >
            <v-row
              class="d-flex flex-column"
              no-gutters
              justify="center"
              align="center"
            >
              <img
                v-if="$vuetify.theme.dark"
                class="ml-4"
                src="@/assets/pandaLogo.svg"
                max-width="180"
              />
              <img
                v-else
                class="ml-4"
                src="@/assets/pandaHappy.svg"
                max-width="180"
              />
              <h3
                class="noFilesAddedText mb-2"
                style="color: #898989; font-size: 28px; font-weight: 500"
                v-html="$t('message.pages.AbTest.emptyTest')"
              ></h3>
              <span
                class="noFilesAddedText"
                style="color: #898989; font-size: 18px"
              >
                {{ $t('message.pages.AbTest.emptyState.addAtLeastTwoVideos') }}
              </span>
              <v-btn
                outlined
                class="mt-5"
                color="primary"
                @click="handleAddVideo"
              >
                {{ $t('message.pages.AbTest.emptyState.addVideoButton') }}
              </v-btn>
              <a class="mt-5" href="#" @click="openAbTestArticle">
                {{ $t('message.pages.AbTest.emptyState.knowMore') }} &raquo;
              </a>
              <img
                class="svgArrowUp pb-16 mb-10"
                src="@/assets/arrowUp.svg"
                style="align-self: flex-end"
              />
            </v-row>
          </v-container>

          <v-row v-else no-gutters>
            <loading-logo :value="250" />
          </v-row>
        </div>
      </span>

      <remove-video-modal
        :isDeletingVideo="loadings.isDeletingVideo"
        :openRemove="openRemove"
        :quantityToRemove="selectedItems.length"
        @close="openRemove = false"
        @remove="handleRemoveVideo"
      />

      <create-modal
        :openCreateModal="openChangeTitleModal"
        :isCreating="loadings.isUpdatingTitle"
        :initialValue="newComparisonName"
        :clearOnClose="false"
        :title="$t('message.pages.AbTest.defineNewTitle')"
        @close="openChangeTitleModal = false"
        @create="(testTitle) => handleUpdateTitle(testTitle)"
      />
      <v-dialog v-model="openCodeModal" width="600px">
        <embed-video
          v-if="openCode"
          type="comparison"
          :onlyPageOptions="true"
          :showMenuEmbed="openCode"
          :video="{
            video_external_id: comparison.id,
            pullzone_name: comparison.pullzone,
            status: 'CONVERTED',
            video_player: `https://player-${comparison.pullzone}.tv.pandavideo.com.br/embed/?v=${comparison.id}`,
          }"
          :videoId="comparison.id"
          :defaultTab="1"
          :additionalTextAreaContent="videosButtonsDivs"
          :isAbTest="true"
          @showEmbed="(val) => (openCode = openCodeModal = val)"
        />
      </v-dialog>
      <add-video
        :openAddVideo="openAddVideo"
        :comparisonVideos="comparison.videos"
        @handleAddVideoToTest="
          (videoToBeAdded) => handleAddVideoToTest(videoToBeAdded)
        "
        @handleDuplicate="(videoToBeAdded) => handleDuplicate(videoToBeAdded)"
        @close="openAddVideo = false"
      />
      <declare-test-winner
        :videoToWinTitle="videoToWin.title ? videoToWin.title : ''"
        :isFinishing="loadings.isFinishing"
        :openDeclareWinner="openDeclareWinner"
        @handleSetWinner="handleSetWinner"
        @close="openDeclareWinner = false"
      />
    </template>
  </page-view>
</template>

<script>
// external imports
import VueVirtualTable from 'vue-virtual-table'
import { mapMutations, mapActions, mapState } from 'vuex'
import {
  addSeconds,
  format,
  differenceInDays,
  subHours,
  addDays,
  subDays,
  subMonths,
  isBefore,
} from 'date-fns'
import VueApexCharts from 'vue-apexcharts'
import generator from 'colors-generator'
import DatePicker from 'vue2-datepicker'
import _ from 'lodash'

// internal imports
import i18n from '@/translations/lang'
import { PANDA_STATUS_PAGE_BR, PANDA_STATUS_PAGE } from '@/config/envs'
import Storage from '@/utils/Storage'
import introMixin from '@/utils/intro.mixin.js'

// API
import videosApi from '@/services/videosApi'
import newAnalyticsAPI from '@/services/newAnalyticsApi'

// Components
import PageView from '@/layout/PageView.vue'
import EmbedVideo from '@/components/EmbedVideo.vue'
import CreateModal from '@/components/CreateModal.vue'
import LoadingLogo from '@/components/LoadingLogo.vue'
import AddVideo from './modals/AddVideo.vue'
import RemoveVideoModal from './modals/RemoveVideoModal.vue'
import DeclareTestWinner from './modals/DeclareTestWinner.vue'

export default {
  name: 'NewTest',
  components: {
    PageView,
    VueVirtualTable,
    EmbedVideo,
    apexchart: VueApexCharts,
    RemoveVideoModal,
    CreateModal,
    DeclareTestWinner,
    AddVideo,
    DatePicker,
    LoadingLogo,
    BaseBtn: () => import('@/components/BaseBtn.vue'),
    BaseHeader: () => import('@/components/BaseHeader.vue'),
  },
  mixins: [introMixin],
  data() {
    const _this = this
    return {
      videosButtonsDivs: '',
      comparisonVideosIds: [],
      unknownErrorCode: null,
      updatingConvertingVideosTimeout: null,
      updateInterval: undefined,
      errors: {
        fetching: false,
      },
      rulesToCreate: {
        required: (v) => (v && !!v) || i18n.t('message.requiredField'),
        maxLength: (v) =>
          (v && v.length <= 255) || i18n.t('message.maximumLength'),
        minLength: (v) =>
          (v && v.length > 2) || i18n.t('message.minimumLength'),
      },
      loadings: {
        isDuplicatingVideo: false,
        isLoadingVideos: true,
        checkConvertingVideos: false,
        isAddingVideo: false,
        isDeletingVideo: false,
        isChangingStatus: false,
        isFinishing: false,
        isUpdatingTitle: false,
        isLoadingAnalytics: false,
        rangeUpdate: false,
      },
      statusLabels: {
        COMPLETED: {
          label: i18n.t('message.pages.AbTest.completed'),
          classColor: 'completed--status-button',
        },
        DRAFT: {
          label: i18n.t('message.pages.AbTest.draft'),
          classColor: 'draft--status-button',
        },
        IN_PROGRESS: {
          label: i18n.t('message.pages.AbTest.inProgress'),
          classColor: 'inprogress--status-button',
        },
      },
      newComparisonName: '',
      openDeclareWinner: false,
      openAddVideo: false,
      selectedItems: [],
      openCodeModal: false,
      openCode: false,
      openChangeTitleModal: false,
      nameOrder: 'asc',
      engageOrder: 'asc',
      playrateOrder: 'asc',
      viewsOrder: 'desc',
      tableHeight: window.innerHeight - 180,
      openRemove: false,
      deleteError: {
        defaultError: false,
      },
      videos: [],
      analyticsDates: [subDays(new Date(), 10), new Date()],
      resizeWindowTimeout: null,
      tableConfig: [
        {
          prop: '_action',
          name: i18n.t('message.name'),
          sortable: true,
          actionName: 'titleCommon',
          width: 140,
          alignItems: 'center',
        },
        {
          prop: '_action',
          name: 'engage',
          actionName: 'engageCommom',
          sortable: true,
          width: 50,
          alignItems: 'start',
        },
        {
          prop: '_action',
          actionName: 'playrateCommom',
          name: 'Playrate',
          sortable: true,
          width: 100,
          alignItems: 'center',
        },
        {
          prop: '_action',
          actionName: 'viewsCommom',
          name: 'Visualizações',
          sortable: true,
          width: 100,
          alignItems: 'center',
        },
      ],
      videoToWin: {},
      incomingRetentionData: [],
      incomingPlayrateData: [],
      retentionChart: {
        yaxisEmptyArray: [7, 6, 5, 4, 3, 2, 1, 0],
        yaxisEmptyArrayIndex: 0,
        yaxisValues: [], // Used when it has no retention data
        maxValue: 0,
        chartOptions: {
          title: {
            text: i18n.t('message.pages.video.metricsPage.retention'),
            align: 'center',
          },
          chart: {
            toolbar: false,
            type: 'area',
            height: 350,
            width: '100%',
            offsetX: 0,
            offsetY: 0,
          },
          dataLabels: {
            enabled: false,
          },
          xaxis: {
            tickAmount: 4,
            labels: {
              rotate: 0,
              formatter: (val) => {
                if (val && val > 0) {
                  if (val > 3600)
                    return format(
                      addSeconds(new Date().setHours(0, 0, 0), val),
                      'HH:mm:ss'
                    )
                  return format(
                    addSeconds(new Date().setHours(0, 0, 0), val),
                    'mm:ss'
                  )
                } else {
                  return format(new Date().setHours(0, 0, 0), 'mm:ss')
                }
              },
            },
          },
          tooltip: {
            enabled: true,
            theme: 'light',
            y: {
              formatter: (val) => {
                if (!val) {
                  return '0 / 0%'
                }
                return `${val} / ${
                  ((val * 100) / _this.retentionChart.maxValue).toFixed(0) + '%'
                }`
              },
            },
          },
          stroke: {
            curve: 'straight',
          },
          colors: ['#FF5732'],
          legend: {
            useSeriesColors: true,
            fontSize: '0',
          },
        },
        chartSeries: [],
      },
      playrateChart: {
        yaxisEmptyArray: [8, 7, 6, 5, 4, 3, 2, 1, 0],
        yaxisEmptyArrayIndex: 0,
        yaxisValues: [], // Used when it has no playrate data
        chartOptions: {
          title: {
            text: i18n.t('message.pages.AbTest.playrate'),
            align: 'center',
          },
          chart: {
            toolbar: false,
            type: 'area',
            height: 350,
            width: '100%',
            offsetX: 0,
            offsetY: 0,
          },
          dataLabels: {
            enabled: false,
          },
          xaxis: {
            type: 'datetime',
            tickAmount: 4,
            labels: {
              rotate: 0,
              formatter: (value) => {
                const date = new Date(value)
                const month = date
                  .toLocaleDateString(
                    _this.locale === 'pt' ? 'pt-BR' : 'en-US',
                    { month: 'long' }
                  )
                  .slice(0, 3)
                const day = date.getDate()
                return `${day} ${month}`
              },
            },
          },
          tooltip: {
            theme: 'light',
            x: {
              formatter: function (value) {
                const date = new Date(value)
                let returnDate = null
                if (_this.locale === 'en') {
                  returnDate = format(date, 'MM/dd/yyyy HH:mm')
                } else {
                  returnDate = format(date, 'dd/MM/yyyy HH:mm')
                }
                return returnDate
              },
            },
            y: {
              formatter: function (
                value,
                // eslint-disable-next-line no-unused-vars
                { seriesIndex, dataPointIndex, series }
              ) {
                // eslint-disable-line
                if (value >= 0) {
                  const max = Math.max(...series.flat())
                  if (value && max) {
                    return `${value} / ${_this.getNumberWithoutEmptyDecimals(
                      (value / max) * 100
                    )}%`
                  }
                }
                return `${value} / 0%`
              },
            },
          },
          stroke: {
            curve: 'straight',
          },
          colors: ['#FF5732'],
          legend: {
            useSeriesColors: true,
            fontSize: '0',
          },
        },
        chartSeries: [],
      },
      headerStyles: {
        name: {
          pt: 'padding-left: 17px; flex: 11.4 1 0%;',
          en: 'padding-left: 17px; flex: 11.7 1 0%;',
        },
        views: {
          pt: 'flex: 6 1 0%;',
          en: 'flex: 5.1 1 0%;',
        },
      },
      analyticsData: {
        keys: new Set(),
        general: new Map(),
        retention: new Map(),
        stats: null,
      },
    }
  },
  watch: {
    nameOrder: function () {
      this.orderByName()
    },
    engageOrder: function () {
      this.orderByEngage()
    },
    playrateOrder: function () {
      this.orderByPlayrate()
    },
    viewsOrder: function () {
      this.orderByViews()
    },
    async analyticsDates([start, end]) {
      if (!start || !end || this.loadings.isLoadingVideos) return

      this.loadings.rangeUpdate = true

      await this.updateNewAnalyticsData()

      this.loadings.rangeUpdate = false
    },
  },
  async mounted() {
    let theme = 'light'

    if (this.$vuetify.theme.dark) theme = 'dark'

    this.retentionChart.chartOptions.tooltip.theme = theme
    this.playrateChart.chartOptions.tooltip.theme = theme

    this.$root.$on('abTestDarkTheme', () => location.reload())

    this.$root.$emit('changeNavItem', 4)

    window.addEventListener('resize', this.resizeTimeout)

    clearInterval(this.updateInterval)
    this.updateInterval = setInterval(this.updateNewAnalyticsData, 60 * 1000)
  },
  async created() {
    const _this = this
    await this.handlePageRequests()
    this.$root.$on('addVideoVideosUpdatedList', (incomingVideos) => {
      if (document.hidden) {
        document.onvisibilitychange = () => {
          if (
            document.visibilityState === 'visible' &&
            _this.convertingVideos.length &&
            _this.$route.name === 'NewTest'
          ) {
            _this.$root.$emit('addVideoVideosUpdatedList', incomingVideos)
          }
        }
      } else if (this.currentStatus === 'DRAFT') {
        _this.updatingConvertingVideosTimeout = setTimeout(() => {
          _this.checkConvertingVideos(incomingVideos)
        }, 5000)
      }
    })
    if (!window.db) {
      this.$root.$emit('initDexie')
    }
  },
  updated() {
    if (
      this.convertingVideos.length &&
      this.$route.name === 'NewTest' &&
      !this.loadings.checkConvertingVideos
    ) {
      this.$root.$emit('reloadAddVideoModalVideos')
    }
  },
  beforeDestroy() {
    clearTimeout(this.updatingConvertingVideosTimeout)
    clearInterval(this.updateInterval)
    window.removeEventListener('resize', this.resizeTimeout)
  },
  methods: {
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    ...mapMutations('comparison', [
      'UPDATE_COMPARISON',
      'UPDATE_COMPARISON_STATUS',
      'UPDATE_WINNER_VIDEO',
      'UPDATE_COMPARISON_NAME',
      'UPDATE_COMPARISON_VIDEOS_LIST',
    ]),
    ...mapActions('comparison', [
      'getComparison',
      'updateComparison',
      'finishComparison',
      'startComparison',
    ]),
    verifyDisabledDate(date) {
      const today = new Date()
      return !isBefore(date, today.setDate(today.getDate() - 1))
    },
    resizeTimeout() {
      clearTimeout(this.resizeWindowTimeout)
      this.resizeWindowTimeout = setTimeout(() => {
        this.tableHeight = window.innerHeight - 180
      }, 300)
    },
    async updateNewAnalyticsData() {
      if (this.currentStatus === 'DRAFT') return

      try {
        const videosIDs = this.videos.map(({ id }) => id)
        const startDate = format(this.analyticsDates[0], 'yyyy-MM-dd')
        const endDate = format(this.analyticsDates[1], 'yyyy-MM-dd')

        this.analyticsData.keys = new Set()
        this.analyticsData.general = new Map()
        this.analyticsData.retention = new Map()

        for (const id of videosIDs) {
          const gData = await newAnalyticsAPI.getVideoDataV2(
            id,
            startDate,
            endDate
          )
          const rData = await newAnalyticsAPI.getVideoMetricsV2(
            id,
            startDate,
            endDate
          )

          this.analyticsData.keys.add(id)
          this.analyticsData.general.set(id, gData.data)
          this.analyticsData.retention.set(id, rData.data)
        }

        this.renderNewAnalyticsData({ hot_reload: true })

        this.videos = Array.from(this.videos)

        if (this.incomingPlayrateData.length) {
          this.$refs.playrateChart.updateSeries(this.incomingPlayrateData)
          this.playrateChart.chartSeries = this.incomingPlayrateData
        }

        if (this.incomingRetentionData.length) {
          this.retentionChart.chartSeries = this.incomingRetentionData
          this.$refs.retentionChart.updateSeries(this.incomingRetentionData)
        }

        this.incomingPlayrateData = []
        this.incomingRetentionData = []
      } catch (error) {
        console.error('error', error)
        this.errors.fetch = true
      }
    },
    openRetentionArticle() {
      if (this.locale === 'pt') {
        return window.open(
          'https://help.pandavideo.com/pt-br/article/como-funcionam-as-metricas-de-retencao-6fm147/',
          '_blank'
        )
      }
      return window.open(
        'https://help.pandavideo.com/en-us/article/how-does-the-retention-graph-work-1b91n27/',
        '_blank'
      )
    },
    openAnalyticsArticle() {
      if (this.locale === 'pt') {
        return window.open(
          'https://help.pandavideo.com/pt-br/article/como-funcionam-as-metricas-do-analytics-na-panda-video-qk2m59/',
          '_blank'
        )
      }
      return window.open(
        'https://help.pandavideo.com/en-us/article/how-do-analytics-metrics-work-in-panda-video-kinu6m/',
        '_blank'
      )
    },
    openAbTestArticle() {
      if (this.locale === 'pt') {
        return window.open(
          'https://help.pandavideo.com/pt-br/article/como-funciona-o-teste-ab-12xaro6/',
          '_blank'
        )
      }
      return window.open(
        'https://help.pandavideo.com/en-us/article/how-the-test-ab-works-1u9l8iv/',
        '_blank'
      )
    },
    checkConvertingVideos(incomingVideos) {
      if (!this.loadings.checkConvertingVideos) {
        this.loadings.checkConvertingVideos = true
      }
      const currentIncomingVideoIds = incomingVideos.map(({ id }) => id)
      const currentComparisonVideosUpdated = this.videos.map((video) => {
        const { id } = video
        const currentVideoPositionInIncomingVideos =
          currentIncomingVideoIds.indexOf(id)
        if (currentVideoPositionInIncomingVideos !== -1) {
          video = incomingVideos[currentVideoPositionInIncomingVideos]
        }
        return video
      })
      const currentConvertingVideos = currentComparisonVideosUpdated.filter(
        ({ status }) => status === 'CONVERTING' || status === 'DRAFT'
      )
      if (currentConvertingVideos.length) {
        this.videos = currentComparisonVideosUpdated
        this.$root.$emit('reloadAddVideoModalVideos')
      } else {
        this.loadings.checkConvertingVideos = false
        this.videos = currentComparisonVideosUpdated
      }
    },
    getSubKey() {
      let subKey = null
      let keys = Storage.getStorageKeys()
      for (let key of keys) {
        if (key[0].includes('LastAuthUser')) {
          subKey = key[1]
        }
      }
      return subKey
    },
    aggregateRetention(data) {
      return Object.keys(data)
        .map((item) => ({
          x: item,
          y: data[item],
        }))
        .sort(function (a, b) {
          return a.x - b.x
        })
    },
    async fetchAnalytics() {
      this.loadings.isLoadingAnalytics = true

      this.setChartColors()
      await this.getNewAnalyticsData()
      this.setRetentionChartYaxis()
      this.setPlayrateChartYaxis()

      this.loadings.isLoadingAnalytics = false
    },
    setRetentionChartYaxis() {
      const valuesArray = [...Object.values(this.retentionChart.chartSeries)]
        .map((value) => value.data.map((val) => val.y))
        .flat()
      this.retentionChart.maxValue = Math.max.apply(null, valuesArray)
      if (this.retentionChart.maxValue !== 0) {
        this.retentionChart.chartOptions.yaxis = {
          min: 0,
          labels: {
            formatter: (val) => {
              if (val) {
                return (
                  ((val * 100) / this.retentionChart.maxValue).toFixed(0) + '%'
                )
              }
              return '0%'
            },
          },
        }
      } else {
        this.retentionChart.chartOptions.yaxis = {
          labels: {
            formatter: () => {
              if (this.retentionChart.yaxisEmptyArrayIndex >= 8) {
                this.retentionChart.yaxisEmptyArrayIndex = 0
              }
              const toBeReturned = `${
                this.retentionChart.yaxisEmptyArray[
                  this.retentionChart.yaxisEmptyArrayIndex
                ]
              }%`
              this.retentionChart.yaxisEmptyArrayIndex++
              return toBeReturned
            },
          },
        }
      }
    },
    setPlayrateChartYaxis() {
      const valuesArray = [...Object.values(this.playrateChart.chartSeries)]
        .map((value) => value.data.map((val) => val.y))
        .flat()
      this.playrateChart.maxValue = Math.max.apply(null, valuesArray)
      if (this.playrateChart.maxValue !== 0) {
        this.playrateChart.chartOptions.yaxis = {
          min: 0,
          labels: {
            formatter: (val) => {
              if (val) {
                return (
                  ((val / this.playrateChart.maxValue) * 100).toFixed(0) + '%'
                )
              }
              return '0%'
            },
          },
        }
      } else {
        this.playrateChart.chartOptions.yaxis = {
          labels: {
            formatter: () => {
              if (this.playrateChart.yaxisEmptyArrayIndex >= 9) {
                this.playrateChart.yaxisEmptyArrayIndex = 0
              }
              const toBeReturned = `${
                this.playrateChart.yaxisEmptyArray[
                  this.playrateChart.yaxisEmptyArrayIndex
                ]
              }%`
              this.playrateChart.yaxisEmptyArrayIndex++
              return toBeReturned
            },
          },
        }
      }
    },
    async getNewAnalyticsData() {
      this.errors.fetch = false

      try {
        const videosIDs = this.videos.map(({ id }) => id)
        const startDate = format(this.analyticsDates[0], 'yyyy-MM-dd')
        const endDate = format(this.analyticsDates[1], 'yyyy-MM-dd')

        this.analyticsData.keys = new Set()
        this.analyticsData.general = new Map()
        this.analyticsData.retention = new Map()

        for (const id of videosIDs) {
          const gData = await newAnalyticsAPI.getVideoDataV2(
            id,
            startDate,
            endDate
          )
          const rData = await newAnalyticsAPI.getVideoMetricsV2(
            id,
            startDate,
            endDate
          )

          this.analyticsData.keys.add(id)
          this.analyticsData.general.set(id, gData.data)
          this.analyticsData.retention.set(id, rData.data)
        }

        this.renderNewAnalyticsData()
      } catch (error) {
        console.error('error', error)
        this.errors.fetch = true
      }
    },
    getNewAnalyticsStats() {
      const { keys, general, retention } = this.analyticsData
      let all_time = []
      let retSize = []

      for (const key of keys) {
        // set general data
        const { views_data } = general.get(key)
        let dataKeys = Object.keys(views_data)

        if (dataKeys.length) all_time.push(...dataKeys)

        // set retention data
        const retentionData = retention.get(key).retention
        dataKeys = Object.keys(retentionData)

        retSize.push(dataKeys.length)
      }

      all_time.sort((a, b) => {
        if (isBefore(new Date(a), new Date(b))) {
          return -1
        }

        return 1
      })

      const max_ret = Math.max(...retSize)

      this.analyticsData.stats = {
        all_time,
        max_ret,
      }

      return this.analyticsData.stats
    },
    getVideoRetentionV2({ length, retention, metadata }) {
      if (length <= 0) return 0

      let maxPlays = 0

      const retentionSum = Object.values(retention).reduce(
        (a, b) => Number(a) + Number(b),
        0
      )

      if (retention)
        maxPlays = Object.values(retention).reduce((a, b) => (a > b ? a : b))

      const averageRetention = this.getNumberWithoutEmptyDecimals(
        ((retentionSum * 5) / maxPlays / metadata.length) * 100
      )

      if (!Number(averageRetention)) {
        if (retentionSum > 0) return 1
        else return 0
      }

      return averageRetention
    },
    getNumberWithoutEmptyDecimals(number) {
      number = number.toFixed(2)
      if (number.split('.')[1] === '00') {
        number = number.split('.')[0]
      }
      return number
    },
    renderNewAnalyticsData({ hot_reload = false } = {}) {
      const { keys, general, retention } = this.analyticsData
      const { all_time, max_ret } = this.getNewAnalyticsStats()
      const [startDate, endDate] = this.analyticsDates

      for (const key of keys) {
        const generalData = general.get(key)
        const retentionData = retention.get(key)

        const video = _.find(this.videos, { id: key })
        const ret_keys = Object.keys(retentionData.retention)
        const time_keys = Object.keys(generalData.views_data)
        const days_diff = differenceInDays(endDate, startDate)

        let viewsData

        /** set video analytics data */
        this.$set(video, 'analytics', {
          views: generalData.total.view,
          uniqueViews: generalData.total.unique_view,
          uniquePlays: generalData.total.unique_play,
          retention: this.getVideoRetentionV2({
            length: ret_keys.length,
            retention: retentionData.retention,
            metadata: video.metadata ?? { length: 0 },
          }),
          playRate: 0,
        })

        /** set playrate */
        if (generalData.total.play && generalData.total.view)
          video.analytics.playRate = this.getNumberWithoutEmptyDecimals(
            (generalData.total.play * 100) / generalData.total.view
          )

        /** set playrate series */
        if (!all_time.length) {
          if (!hot_reload)
            this.setFakePlayrateV2({
              days_diff,
              video,
            })
        } else if (all_time.length < 2)
          this.setFakePlayrateWithOneDateV2({
            all_time,
            hot_reload,
            data: generalData.views_data,
            video,
          })
        else {
          viewsData = this.getPlayrateSeries(
            time_keys,
            all_time,
            generalData.views_data
          )

          const playrateData = Object.keys(viewsData)
            .map((item) => ({
              x: `${subHours(
                new Date(item.replace(/-/gm, '/')),
                (new Date().getTimezoneOffset() / 60) * 2
              ).toISOString()}`,
              y: viewsData[item].play,
            }))
            .sort(function (a, b) {
              return new Date(a.x) - new Date(b.x)
            })

          const playRateObject = {
            name: video.title,
            data: playrateData,
          }

          if (hot_reload) this.incomingPlayrateData.push(playRateObject)
          else this.playrateChart.chartSeries.push(playRateObject)
        }

        /** set retention chart series */
        if (!max_ret) {
          if (!hot_reload)
            this.setFakeRetentionV2({
              days_diff,
              video,
            })
        } else
          this.setRetentionChartSeriesV2({
            data: retentionData.retention,
            ret_keys,
            video,
            hot_reload,
          })
      }
    },
    setFakeRetentionV2({ days_diff, video }) {
      const obj = {}
      let k = 0

      for (let i = 0; i <= days_diff; i++) {
        obj[i] = { x: `${k}`, y: 0 }
        k += 5
      }

      this.retentionChart.chartSeries.push({
        name: video.title,
        data: Object.values(obj),
      })
    },
    setFakePlayrateV2({ days_diff, video }) {
      const [startDate] = this.analyticsDates

      const fakeTrafficArray = {}

      for (let i = 0; i < days_diff; i++) {
        const dateIndex = format(addDays(startDate, i), 'yyyy-MM-dd HH:mm')

        fakeTrafficArray[dateIndex] = {
          play: 0,
          unique_view: 0,
          view: 0,
        }
      }

      this.playrateChart.chartSeries.push({
        name: video.title,
        data: Object.keys(fakeTrafficArray)
          .map((item) => ({
            x: subHours(
              new Date(item.replace(/-/gm, '/')),
              (new Date().getTimezoneOffset() / 60) * 2
            ).toISOString(),
            y: fakeTrafficArray[item].play,
          }))
          .sort(function (a, b) {
            return new Date(a.x) - new Date(b.x)
          }),
      })
    },
    setFakePlayrateWithOneDateV2({ data, all_time, video, hot_reload }) {
      const beforeDay = subDays(new Date(all_time[0]), 1)
      const views_data = _.cloneDeep(data)

      let dates = [format(new Date(beforeDay), 'yyyy-MM-dd HH:mm'), ...all_time]

      dates.forEach((dateIndex) => {
        if (!views_data.hasOwnProperty(`${dateIndex}`))
          views_data[dateIndex] = {
            play: 0,
            unique_view: 0,
            view: 0,
          }
      })

      const playrateData = Object.keys(views_data)
        .map((item) => ({
          x: subHours(
            new Date(item.replace(/-/gm, '/')),
            (new Date().getTimezoneOffset() / 60) * 2
          ).toISOString(),
          y: views_data[item].play,
        }))
        .sort(function (a, b) {
          return new Date(a.x) - new Date(b.x)
        })

      const playRateObject = {
        name: video.title,
        data: playrateData,
      }

      if (hot_reload) this.incomingPlayrateData.push(playRateObject)
      else this.playrateChart.chartSeries.push(playRateObject)
    },
    setRetentionChartSeriesV2({ data, ret_keys, video, hot_reload }) {
      // if retention series has no data yet
      if (!this.retentionChart.chartSeries.length && ret_keys.length) {
        const retention = this.getFilledRetentionV2({ data })

        this.retentionChart.chartSeries = [
          {
            name: video.title,
            data: this.aggregateRetention(retention),
          },
        ]
      }

      // if current retention is low in data
      else if (ret_keys.length <= 1) {
        const retention = this.getFilledRawRetentionV2()

        const retentionObject = {
          name: video.title,
          data: Object.values(retention),
        }

        if (hot_reload) this.incomingRetentionData.push(retentionObject)
        else this.retentionChart.chartSeries.push(retentionObject)
      } else {
        const retention = this.getFilledRetentionV2({ data })

        const retentionObject = {
          name: video.title,
          data: this.aggregateRetention(retention),
        }

        if (hot_reload) this.incomingRetentionData.push(retentionObject)
        else this.retentionChart.chartSeries.push(retentionObject)
      }
    },
    getFilledRetentionV2({ data }) {
      const { keys, retention: rData } = this.analyticsData
      let retention = _.cloneDeep(data)
      let maxCount = 0

      for (const key of keys) {
        const { length } = Object.keys(rData.get(key).retention)
        if (length > maxCount) maxCount = length
      }

      if (Object.keys(retention).length < maxCount) {
        let obj = {}
        let k = (Object.keys(retention).length - 1) * 5

        for (let i = Object.keys(retention).length - 1; i < maxCount; i++) {
          obj[k] = 0
          k += 5
        }

        retention = { ...retention, ...obj }
      }

      return retention
    },
    getFilledRawRetentionV2() {
      const { keys, retention } = this.analyticsData

      const obj = {}
      let maxCount = 0

      for (const key of keys) {
        const { length } = Object.keys(retention.get(key).retention)
        if (length > maxCount) maxCount = length
      }

      let k = 0
      for (let i = 0; i < maxCount; i++) {
        obj[i] = { x: `${k}`, y: 0 }
        k += 5
      }

      return obj
    },
    getPlayrateSeries(timeDataKeys, allTimeData, timeData) {
      const maxCount = allTimeData.length
      const currentCount = timeDataKeys.length
      if (currentCount < maxCount) {
        for (let i of allTimeData) {
          if (timeDataKeys.indexOf(i) !== -1) {
            continue
          }
          timeData[i] = { play: 0 }
        }
      }
      return timeData
    },
    getHeaderStyle(type) {
      const locale = this.$i18next.resolvedLanguage
      return this.headerStyles[type][locale]
    },
    findColor(id) {
      const videosId = this.videos.map(({ id }) => id)
      const index = videosId.indexOf(id)
      return this.retentionChart.chartOptions.colors[index]
    },
    handleGoToVideo(id) {
      this.$router.push({ name: 'VideoPage', params: { id } })
      this.$root.$emit('changeNavItem', 0)
    },
    handleSnackbar(item) {
      if (!item) {
        item = i18n.t('message.navDrawer.successMessage')
      }
      this.UPDATE_GLOBAL_SNACKBAR({
        open: true,
        text: item,
      })
    },
    async handleUpdateTitle(testTitle) {
      try {
        this.loadings.isUpdatingTitle = true
        if (testTitle !== this.comparison.name) {
          this.comparisonVideosIds = this.comparison.videos.map(
            (video) => video.id
          )
          const { comparisonObjectRequest } = this.getComparisonRequestObject()
          comparisonObjectRequest.name = testTitle
          await this.updateComparison([
            comparisonObjectRequest,
            this.comparison.id,
          ])
          this.UPDATE_COMPARISON_NAME(testTitle)
          this.newComparisonName = testTitle
        }
        this.handleSnackbar(
          i18n.t('message.pages.AbTest.actionResponses.titleUpdated')
        )
        this.openChangeTitleModal = false
      } catch (e) {
        console.error(e)
        this.handleSnackbar(i18n.t('message.pages.AbTest.errors.updVideo'))
      }
      this.loadings.isUpdatingTitle = false
    },
    getStatus(status) {
      return this.statusLabels[status]
    },
    async handleRemoveVideo() {
      try {
        this.loadings.isDeletingVideo = true
        this.comparisonVideosIds = this.comparison.videos.map(
          (video) => video.id
        )
        const selectedItemsId = this.selectedItems.map((item) => item.id)
        const itemsToBeKeep = this.comparisonVideosIds.filter((videoId) => {
          const selectedIndex = selectedItemsId.indexOf(videoId)
          if (selectedIndex === -1) return videoId
          return false
        })
        const newComparisonVideoList = this.comparison.videos.filter(
          (video) => {
            if (itemsToBeKeep.indexOf(video.id) !== -1) return video
            return false
          }
        )
        this.UPDATE_COMPARISON_VIDEOS_LIST(newComparisonVideoList)
        this.comparisonVideosIds = itemsToBeKeep
        const { comparisonObjectRequest } = this.getComparisonRequestObject()
        await this.updateComparison([
          comparisonObjectRequest,
          this.comparison.id,
        ])
        this.videos = this.comparison.videos
        this.loadings.isDeletingVideo = false
        this.openRemove = false
        this.selectedItems = this.selectedItems.filter((video) => {
          if (itemsToBeKeep.indexOf(video.id) !== -1) return video
          return false
        })
      } catch (e) {
        console.error(e)
        this.handleSnackbar(i18n.t('message.pages.AbTest.errors.rmVideo'))
        this.loadings.isDeletingVideo = false
        this.openRemove = false
      }
    },
    async handlePageRequests() {
      try {
        await this.getComparison(this.$route.params.id)
        this.newComparisonName = this.comparison.name
        this.videos = this.comparison.videos
        this.errors.fetching = false
        if (this.currentStatus !== 'DRAFT') {
          this.checkForVideoButtons()
          await this.fetchAnalytics()
        }
        this.loadings.isLoadingVideos = false

        this.setIntroJS({ key: 'test-ab-page', delay: 1500 })
      } catch (e) {
        console.error(e)
        if (e.code) {
          this.unknownErrorCode = e.code
        }
        this.errors.fetching = true
        this.loadings.isLoadingVideos = false
      }
    },
    checkForVideoButtons() {
      const divIds = []
      this.videos.forEach(({ config }) => {
        if (config.hasOwnProperty('outsideCta')) {
          const outsideCtas = config.outsideCta
          outsideCtas.forEach((cta) => {
            if (cta.hasOwnProperty('divId')) {
              divIds.push(cta.divId)
            }
          })
        }
      })
      if (divIds.length) {
        let videosButtonsDivs = ''
        divIds.forEach((id) => {
          videosButtonsDivs += `<div id="${id}"></div>`
        })
        this.videosButtonsDivs = videosButtonsDivs
      }
    },
    setChartColors() {
      const colors = generator
        .generate('#478ef7', this.comparison.videos.length)
        .get()
      this.retentionChart.chartOptions.colors = colors
      this.playrateChart.chartOptions.colors = colors
      this.videos = this.videos.map((video, index) => ({
        ...video,
        color: colors[index],
      }))
    },
    handleStatusPage() {
      if (this.locale === 'pt')
        return window.open(PANDA_STATUS_PAGE_BR, '_blank')
      return window.open(PANDA_STATUS_PAGE, '_blank')
    },
    getComparisonRequestObject() {
      return {
        comparisonObjectRequest: {
          name: this.comparison.name,
          description: this.comparison.description,
          video_ids: this.comparisonVideosIds,
        },
      }
    },
    async handleAddVideoToTest(videoToBeAdded) {
      try {
        this.loadings.isAddingVideo = true
        this.comparisonVideosIds = this.comparison.videos.map(
          (video) => video.id
        )
        this.comparisonVideosIds.push(videoToBeAdded.id)
        const { comparisonObjectRequest } = this.getComparisonRequestObject()
        await this.updateComparison([
          comparisonObjectRequest,
          this.comparison.id,
        ])
        this.videos.push(videoToBeAdded)
        this.loadings.isAddingVideo = false
        this.openAddVideo = false
      } catch (e) {
        console.error(e)
        this.loadings.isAddingVideo = false
        this.handleSnackbar(i18n.t('message.pages.AbTest.errors.addVideo'))
      }
    },
    async handleDuplicate(videoToBeAdded) {
      this.loadings.isDuplicatingVideo = true
      try {
        const { data: clonedVideo } = await videosApi.cloneVideo(
          videoToBeAdded.id
        )
        videoToBeAdded.id = clonedVideo.id
        videoToBeAdded.title = clonedVideo.title
        this.comparisonVideosIds = this.comparison.videos.map(
          (video) => video.id
        )
        this.comparisonVideosIds.push(videoToBeAdded.id)
        const { comparisonObjectRequest } = this.getComparisonRequestObject()
        await this.updateComparison([
          comparisonObjectRequest,
          this.comparison.id,
        ])
        if (!clonedVideo.folder_id) {
          clonedVideo.folder_id = 'root'
        }
        const videoFormat = {
          title: clonedVideo.title,
          id: clonedVideo.id,
          status: clonedVideo.status,
          folder_id: clonedVideo.folder_id,
          updated_at: clonedVideo.updated_at,
        }
        await window.db.videos.add(videoFormat)
        this.videos.push(videoToBeAdded)
        this.openAddVideo = false
        this.$root.$emit('forceStopAddVideoLoading')
        this.$root.$emit('reloadAddVideoModalVideos')
      } catch (e) {
        if (e.response.status === '504') {
          this.$root.$emit('reloadAddVideoModalVideos')
        }
        console.error(e)
        this.$root.$emit('forceStopAddVideoLoading')
        this.handleSnackbar(i18n.t('message.pages.AbTest.errors.addVideo'))
      }
      this.loadings.isDuplicatingVideo = false
    },
    handleAddVideo() {
      this.openAddVideo = true
    },
    handleDelete() {
      this.openRemove = true
    },
    async handleSetWinner() {
      try {
        this.loadings.isFinishing = true
        await this.finishComparison([this.comparison.id, this.videoToWin.id])
        this.handleSnackbar(
          i18n.t('message.pages.AbTest.actionResponses.finishTest')
        )
        this.openDeclareWinner = false
        this.loadings.isFinishing = false
      } catch (e) {
        console.error(e)
        this.handleSnackbar(i18n.t('message.pages.AbTest.errors.finishTest'))
        this.loadings.isFinishing = false
      }
    },
    handleOpenWinnerModal(index) {
      this.videoToWin = this.videos[index]
      this.openDeclareWinner = true
    },
    async handleInitTest() {
      try {
        this.loadings.isChangingStatus = true
        await this.startComparison(this.comparison.id)
        await this.fetchAnalytics()
        this.videos = this.comparison.videos
        this.UPDATE_COMPARISON_STATUS('IN_PROGRESS')
        this.checkForVideoButtons()
        this.loadings.isChangingStatus = false
        this.handleSnackbar(
          i18n.t('message.pages.AbTest.actionResponses.startTest')
        )
      } catch (e) {
        console.error(e)
        this.handleSnackbar(i18n.t('message.pages.AbTest.errors.startTest'))
        this.loadings.isChangingStatus = false
      }
    },
    selectAllItems() {
      if (this.selectedItems.length === this.videos.length) {
        this.selectedItems = []
        return
      }
      this.selectedItems = [...this.videos]
    },
    checkboxSelection(item) {
      let alreadySelected = this.checkIfSelected(item)
      if (alreadySelected) {
        let newArr = [
          ...this.selectedItems.filter((lookFolder) => {
            return lookFolder.id !== item.id
          }),
        ]
        this.selectedItems = [...newArr]
      } else {
        this.selectedItems.push(item)
      }
    },
    checkIfSelected(item) {
      let foundItem = this.selectedItems.find((searchedItem) => {
        return searchedItem.id === item.id
      })
      if (foundItem) return true
      return false
    },
    handleBackToListing() {
      this.$router.push({ name: 'AbTest' })
    },
    handleOpenCodeModal() {
      this.openCodeModal = true
      setTimeout(
        function () {
          this.openCode = true
        }.bind(this)
      )
    },
    getDateWithZero(number) {
      if (number < 10) return `0${number}`
      return number
    },
    handleOrderByPlayrate() {
      if (!this.playrateOrder || this.playrateOrder === 'desc')
        return (this.playrateOrder = 'asc')
      if (this.playrateOrder === 'asc') return (this.playrateOrder = 'desc')
    },
    handleOrderByViews() {
      if (!this.viewsOrder || this.viewsOrder === 'desc')
        return (this.viewsOrder = 'asc')
      if (this.viewsOrder === 'asc') return (this.viewsOrder = 'desc')
    },
    orderByEngage() {
      if (this.engageOrder === 'asc') {
        this.videos.sort(
          (a, b) =>
            Number(a.analytics.retention) - Number(b.analytics.retention)
        )
      }
      if (this.engageOrder === 'desc') {
        this.videos.sort(
          (a, b) =>
            -(Number(a.analytics.retention) - Number(b.analytics.retention))
        )
      }
    },
    orderByViews() {
      if (this.viewsOrder === 'asc') {
        this.videos.sort((a, b) => a.analytics.views - b.analytics.views)
      }
      if (this.viewsOrder === 'desc') {
        this.videos.sort((a, b) => -(a.analytics.views - b.analytics.views))
      }
    },
    orderByPlayrate() {
      if (this.playrateOrder === 'asc') {
        this.videos.sort(
          (a, b) => Number(a.analytics.playRate) - Number(b.analytics.playRate)
        )
      }
      if (this.playrateOrder === 'desc') {
        this.videos.sort(
          (a, b) =>
            -(Number(a.analytics.playRate) - Number(b.analytics.playRate))
        )
      }
    },
    handleorderByEngage() {
      if (!this.engageOrder || this.engageOrder === 'desc')
        return (this.engageOrder = 'asc')
      if (this.engageOrder === 'asc') return (this.engageOrder = 'desc')
    },
    orderByName() {
      if (this.nameOrder === 'asc') {
        this.videos.sort((a, b) =>
          a.title.localeCompare(b.title, undefined, { numeric: true })
        )
      }
      if (this.nameOrder === 'desc') {
        this.videos.sort(
          (a, b) =>
            -a.title.localeCompare(b.title, undefined, { numeric: true })
        )
      }
    },
    handleOrderByName() {
      if (!this.nameOrder || this.nameOrder === 'desc')
        return (this.nameOrder = 'asc')
      if (this.nameOrder === 'asc') return (this.nameOrder = 'desc')
    },
  },
  computed: {
    ...mapState({
      comparison: (state) => state.comparison.comparison,
    }),
    dateRangeValue() {
      const mask = (date) => format(date, 'dd/MM/yyyy')
      const [start, end] = _.isArray(this.analyticsDates)
        ? this.analyticsDates
        : [this.analyticsDates, this.analyticsDates]

      return `${mask(start)} ~ ${mask(end)}`
    },
    shortcuts() {
      const i18n = this.$i18next
      return [
        {
          text: i18n.t('message.pages.video.metricsPage.datepicker.yesterday'),
          onClick() {
            const date = new Date()
            return [subDays(date, 1), date]
          },
        },
        {
          text: i18n.t('message.pages.video.metricsPage.datepicker.7days'),
          onClick() {
            const date = new Date()
            return [subDays(date, 7), date]
          },
        },
        {
          text: i18n.t('message.pages.video.metricsPage.datepicker.aMonth'),
          onClick() {
            const date = new Date()

            return [subMonths(date, 1), date]
          },
        },
      ]
    },
    hasEditAbTestPerm() {
      return (
        this.$store.state.user?.userPermissions?.includes('put-test-a-b') ??
        false
      )
    },
    canTestBeStarted: {
      get() {
        if (
          !this.canPost ||
          this.loadings.isChangingStatus ||
          this.convertingVideos.length
        ) {
          return false
        }
        return true
      },
      cache: false,
    },
    shouldShowListingAnalytics() {
      return this.currentStatus !== 'DRAFT' && !this.loadings.isChangingStatus
    },
    convertingVideos() {
      return this.videos.filter(
        ({ status }) => status === 'CONVERTING' || status === 'DRAFT'
      )
    },
    winnerVideoId: {
      get() {
        if (this.comparison?.winner_video_id)
          return this.comparison.winner_video_id
        return ''
      },
      cache: false,
    },
    currentStatus: {
      get() {
        if (this.comparison?.status) return this.comparison.status
        return 'DRAFT'
      },
      cache: false,
    },
    isTestStarted() {
      if (this.comparison.status === 'DRAFT') return false
      return true
    },
    anyItemSelected() {
      return this.selectedItems.length > 0
    },
    checkboxIndeterminateStatus() {
      return (
        this.selectedItems.length > 0 &&
        this.selectedItems.length < this.videos.length
      )
    },
    canPost() {
      if (this.videos.length > 1) return true
      return false
    },
    locale() {
      return this.$i18next.resolvedLanguage
    },
  },
}
</script>

<style lang="scss">
@import '~vue2-datepicker/scss/index.scss';

.testABPickerField {
  background-color: #373b54;
}

.testABDatePicker {
  .mx-datepicker-btn-confirm {
    background-color: var(--v-brand-base);
    color: var(--v-neutral-lighten4);

    &:hover {
      background-color: var(--v-brand-darken1);
      border-color: var(--v-brand-darken1);
    }

    &:active {
      background-color: var(--v-brand-darken2);
      border-color: var(--v-brand-darken2);
    }
  }
}

.new-test
  .v-dialog__content.v-dialog__content--active
  .v-dialog.v-dialog--active {
  overflow: hidden !important;
}

.mini-chip.v-chip.v-size--default {
  height: 23px;
  margin-bottom: 2px;
}

.t-header {
  display: none;
}

.t-header span {
  font-weight: bold;
}

.new-test div.item-cell div.item-cell-inner.rowSlot {
  align-items: center !important;
  justify-content: flex-start !important;
}
</style>

<style scoped>
.chart-container,
.the-retention-chart {
  position: relative;
}

.the-retention-chart {
  z-index: 1;
}

.full-zero-retention {
  padding: 0 53px 0px 4.1%;
}

.full-zero-retention-min-total {
  padding: 0 53px 0px 3%;
}

.grid-widget-container {
  display: flex;
  flex-direction: row;
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.grid-widget {
  flex: 1;
  box-sizing: border-box;
  padding: 0;
  height: 100%;
  min-height: 0;
  width: 100%;
  overflow: hidden;
}
.svgArrowUp {
  position: relative;
  bottom: 402px;
  right: 0px;
}

.edit-title {
  opacity: 0;
}

.container-title:hover .edit-title {
  opacity: 1;
}

.item-line:hover .declare-winner-button {
  display: block;
}

.item-line:hover .views-info,
.declare-winner-button {
  display: none;
}
</style>
